import { log } from '@common/core-layer/services/logger.service'

/**
 * Query param used to carry the clicked notification's `refId` across a
 * cold-start push click (service worker `clients.openWindow` → fresh app boot).
 *
 * The service worker builds the same param inline (it cannot import modules),
 * see `server/routes/cp-firebase-messaging-sw.ts`.
 */
export const NOTIFICATION_REF_ID_QUERY_PARAM = 'notificationRefId'

/** Upper bound on an accepted `refId` — guards against unbounded URL-supplied values. */
export const MAX_NOTIFICATION_REF_ID_LENGTH = 256

/**
 * Normalizes a `refId` coming from an untrusted source (SW postMessage payload,
 * URL query). Returns `null` when it is absent, empty or implausibly long.
 */
export function normalizeNotificationRefId(value: unknown): string | null {
  const candidate = Array.isArray(value) ? value[0] : value
  if (typeof candidate !== 'string') return null

  const trimmed = candidate.trim()
  if (!trimmed || trimmed.length > MAX_NOTIFICATION_REF_ID_LENGTH) return null

  return trimmed
}

/**
 * Appends the notification `refId` to an absolute URL, preserving any existing
 * query string. Returns the input unchanged when the `refId` is unusable.
 */
export function appendNotificationRefIdToUrl(url: string, refId: unknown): string {
  const normalizedRefId = normalizeNotificationRefId(refId)
  if (!normalizedRefId) return url

  try {
    const parsed = new URL(url)
    parsed.searchParams.set(NOTIFICATION_REF_ID_QUERY_PARAM, normalizedRefId)
    return parsed.href
  } catch {
    return url
  }
}

/**
 * sessionStorage key holding a clicked notification's `refId` while the read is
 * waiting for authentication.
 *
 * An unauthenticated cold start can be redirected or fully reloaded by login
 * middleware before an in-memory watcher fires, which would lose the only copy of
 * the refId and with it the read acknowledgement.
 */
export const PENDING_NOTIFICATION_REF_ID_KEY = 'fcm_pending_notification_ref_id'

/** Safe access to `sessionStorage` — returns null on SSR or when storage is unavailable. */
function pendingRefIdStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null
    return window.sessionStorage
  } catch {
    return null
  }
}

export function storePendingNotificationRefId(refId: string): void {
  try {
    pendingRefIdStorage()?.setItem(PENDING_NOTIFICATION_REF_ID_KEY, refId)
  } catch (err) {
    log.warn('[FCM] Unable to persist the pending notification refId', { error: err instanceof Error ? err : undefined })
  }
}

export function readPendingNotificationRefId(): string | null {
  try {
    return normalizeNotificationRefId(pendingRefIdStorage()?.getItem(PENDING_NOTIFICATION_REF_ID_KEY))
  } catch (err) {
    log.warn('[FCM] Unable to read the pending notification refId', { error: err instanceof Error ? err : undefined })
    return null
  }
}

/**
 * Clears the pending entry only when it still holds `refId`.
 *
 * Two notifications can be clicked in quick succession: the second overwrites the stored
 * refId while the first read is still in flight. An unconditional clear from the first
 * read would drop the second notification's retry state and leave it unread forever.
 */
export function clearPendingNotificationRefIdIf(refId: string): boolean {
  if (readPendingNotificationRefId() !== refId) return false
  return clearPendingNotificationRefId()
}

/**
 * Returns false when the entry could not be removed — the read already succeeded, so a
 * stale entry would otherwise make later boots retry the same refId forever, invisibly.
 */
export function clearPendingNotificationRefId(): boolean {
  try {
    pendingRefIdStorage()?.removeItem(PENDING_NOTIFICATION_REF_ID_KEY)
    return true
  } catch (err) {
    log.warn('[FCM] Unable to clear the pending notification refId — it may be retried on the next boot', {
      error: err instanceof Error ? err : undefined,
    })
    return false
  }
}
